import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CONTACT } from "@/data/site";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", destination: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you! Our travel expert will reach out within 24 hours.");
      setForm({ name: "", phone: "", email: "", destination: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 md:py-28 px-5 md:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center flex flex-col items-center">
          <SectionHeading
            eyebrow="Contact Us"
            title="Let's plan your dream trip together"
            subtitle="Reach out for custom itineraries, group discounts, or a quick question — we're always happy to help."
            align="center"
          />
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-stretch">
          {/* Info + Map */}
          <Reveal>
            <div className="space-y-5 h-full flex flex-col">
              <div className="bg-[#F8FAFC] rounded-2xl p-6 border border-gray-100">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <a href={`tel:${CONTACT.phone.replace(/\s/g,'')}`} data-testid="contact-phone-link" className="group">
                    <div className="flex flex-col items-start gap-2">
                      <div className="w-11 h-11 rounded-xl bg-[#0B3D91] flex items-center justify-center group-hover:bg-[#F9B233] transition-colors">
                        <Phone className="w-5 h-5 text-white" />
                      </div>
                      <p className="text-[11px] uppercase tracking-wider text-gray-500">Phone</p>
                      <p className="text-sm font-semibold text-[#222]">{CONTACT.phone}</p>
                    </div>
                  </a>
                  <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="group">
                    <div className="flex flex-col items-start gap-2">
                      <div className="w-11 h-11 rounded-xl bg-[#0B3D91] flex items-center justify-center group-hover:bg-[#F9B233] transition-colors">
                        <Mail className="w-5 h-5 text-white" />
                      </div>
                      <p className="text-[11px] uppercase tracking-wider text-gray-500">Email</p>
                      <p className="text-sm font-semibold text-[#222] break-all">{CONTACT.email}</p>
                    </div>
                  </a>
                  <div>
                    <div className="flex flex-col items-start gap-2">
                      <div className="w-11 h-11 rounded-xl bg-[#0B3D91] flex items-center justify-center">
                        <MapPin className="w-5 h-5 text-white" />
                      </div>
                      <p className="text-[11px] uppercase tracking-wider text-gray-500">Office</p>
                      <p className="text-sm font-semibold text-[#222]">{CONTACT.address}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl overflow-hidden border border-gray-100 flex-1 min-h-[300px]">
                <iframe
                  title="Malviya Travel Location"
                  data-testid="contact-map"
                  src="https://www.google.com/maps?q=Delhi,India&output=embed"
                  width="100%"
                  height="100%"
                  className="w-full h-full min-h-[300px]"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </Reveal>

          {/* Form */}
          <Reveal delay={100}>
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="bg-white rounded-2xl border border-gray-100 shadow-[0_8px_40px_-15px_rgba(11,61,145,0.2)] p-6 md:p-8 h-full flex flex-col"
            >
              <h3 className="text-xl font-bold text-[#0B3D91]">Send us a message</h3>
              <p className="text-sm text-gray-500 mt-1">We reply within 24 hours.</p>

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-[#0B3D91] uppercase tracking-wide">Name *</Label>
                  <Input
                    data-testid="contact-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Your full name"
                    className="h-11 border-gray-200 focus:border-[#0B3D91]"
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-[#0B3D91] uppercase tracking-wide">Phone *</Label>
                  <Input
                    data-testid="contact-phone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+91"
                    className="h-11 border-gray-200 focus:border-[#0B3D91]"
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-[#0B3D91] uppercase tracking-wide">Email *</Label>
                  <Input
                    data-testid="contact-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="you@example.com"
                    className="h-11 border-gray-200 focus:border-[#0B3D91]"
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-[#0B3D91] uppercase tracking-wide">Destination</Label>
                  <Input
                    data-testid="contact-destination"
                    value={form.destination}
                    onChange={(e) => setForm({ ...form, destination: e.target.value })}
                    placeholder="Where would you like to go?"
                    className="h-11 border-gray-200 focus:border-[#0B3D91]"
                  />
                </div>
              </div>

              <div className="mt-4 space-y-1.5 flex-1 flex flex-col">
                <Label className="text-xs font-semibold text-[#0B3D91] uppercase tracking-wide">Message *</Label>
                <Textarea
                  data-testid="contact-message"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Tell us about your trip..."
                  className="border-gray-200 focus:border-[#0B3D91] min-h-[130px] flex-1"
                  required
                />
              </div>

              <Button
                type="submit"
                data-testid="contact-form-submit"
                disabled={loading}
                className="mt-6 bg-[#0B3D91] hover:bg-[#082d6d] text-white rounded-full h-12 px-8 font-semibold shadow-md hover:shadow-lg hover:scale-[1.02] transition-all disabled:opacity-70"
              >
                <Send className="w-4 h-4 mr-2" /> {loading ? "Sending..." : "Submit"}
              </Button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
